import {ReactNode} from 'react';
import {css} from 'styled-components';

import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {SkeletonColorSchemesEnum} from '../skeleton';
import {
  VirtualTableColumnCellsEnum,
  virtualTableRowOffset,
  VirtualTableSortableColumnDirectionsEnum,
} from './helpers/tableHelpers';

/*
 * Props.
 */

export interface TableColumn<T, M = any> {
  id: string;
  title: ReactNode;
  type: T;

  /** Used for the placeholder loading */
  cellType: VirtualTableColumnCellsEnum;
  settings?: TableColumnSettings;
  metadata?: M;
}

export interface DynamicTableColumn<T> {
  type: T;

  /** Used for the placeholder loading */
  cellType: VirtualTableColumnCellsEnum;
  settings?: TableColumnSettings;
}

export interface TableColumnSettings {
  cellWidth?: TableCellWidthSettings;
  loading?: TableCellLoadingSettings;
  sticky?: TableCellStickySettings;
  tooltip?: ReactNode;
  interactionId?: string;
}

export interface TableCellLoadingSettings {
  style?: SkeletonColorSchemesEnum;
  avatarSize?: VisualSizesEnum;
  iconSize?: VisualSizesEnum;
}
export interface TableCellWidthSettings {
  shouldFillRemainingWidth?: boolean;
  minWidth?: number;
  maxWidth?: number;
  width?: number;
}

export interface TableCellStickySettings {
  isSticky?: boolean;
  stickyOffset?: number;
}

export interface SortableTableColumn {
  id: string;
  directions: ReadonlyArray<VirtualTableSortableColumnDirectionsEnum>;
}

export interface SelectedColumnSort {
  id: string;
  direction: VirtualTableSortableColumnDirectionsEnum;
}

/*
 * Helpers.
 */

export function getWidthSettingsStyles(settings?: TableCellWidthSettings, index?: number) {
  // If we are at the first cell, we need to add some width for the left offset
  const firstCellOffset = index === 0 ? virtualTableRowOffset : 0;
  return css`
    ${settings?.shouldFillRemainingWidth && 'flex: 1;'};
    ${maybeAddMaxWidth(firstCellOffset, settings)};
    ${settings?.minWidth && `min-width: ${settings.minWidth + firstCellOffset}px;`};
    ${settings?.width &&
    `width: ${settings.width + firstCellOffset}px; flex: 0 0 ${settings.width + firstCellOffset}px;`};
  `;
}

// If shouldFillRemainingWidth is set and max width is set, ignore it here as TableCell will handle it
function maybeAddMaxWidth(firstCellOffset: number, settings?: TableCellWidthSettings) {
  if (!settings || !settings.maxWidth) {
    return '';
  }
  if (settings.shouldFillRemainingWidth) {
    return '';
  }
  return `max-width: ${settings.maxWidth + firstCellOffset}px;`;
}

export function getStickyStyles(settings?: TableCellStickySettings) {
  return (
    settings?.isSticky &&
    css`
      position: sticky;
      left: ${settings?.stickyOffset || 0}px;
      z-index: 1;
    `
  );
}

export function convertDynamicColumnToColumn<T, M = any>(
  id: string,
  title: ReactNode,
  dynamicColumn: DynamicTableColumn<T>,
  metadata?: M,
): TableColumn<T, M> {
  return {
    ...dynamicColumn,
    id,
    title,
    metadata,
  };
}
